import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { propertyService } from '../../../services/propertyService';
import { useAuth } from '../../../contexts/AuthContext';

const AllPropertiesModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadProperties();
    }
  }, [isOpen, user?.id]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const data = await propertyService?.getAll(user?.id);
      setProperties(data || []);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties?.filter(property => {
    const searchLower = searchTerm?.toLowerCase();
    return (
      property?.title?.toLowerCase()?.includes(searchLower) ||
      property?.address?.toLowerCase()?.includes(searchLower) ||
      property?.propertyType?.toLowerCase()?.includes(searchLower)
    );
  });

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
  };

  const getPropertyTypeLabel = (type) => {
    const types = {
      apartment: 'شقة',
      villa: 'فيلا',
      office: 'مكتب',
      shop: 'محل تجاري',
      land: 'أرض'
    };
    return types?.[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'text-success bg-success/10',
      rented: 'text-warning bg-warning/10',
      sold: 'text-error bg-error/10'
    };
    return colors?.[status] || 'text-muted-foreground bg-muted/10';
  };

  const getStatusLabel = (status) => {
    const labels = {
      available: 'متاح',
      rented: 'مؤجر',
      sold: 'مباع'
    };
    return labels?.[status] || status;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="جميع العقارات"
      size="large"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Icon
            name="Search"
            size={20}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="ابحث عن عقار..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pr-10 pl-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Properties List */}
        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground mt-2">جاري التحميل...</p>
          </div>
        ) : filteredProperties?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Home" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">لا توجد عقارات</p>
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto space-y-2">
            {filteredProperties?.map((property) => (
              <div
                key={property?.id}
                onClick={() => handlePropertyClick(property)}
                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  selectedProperty?.id === property?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Home" size={18} className="text-primary" />
                      <h4 className="font-semibold text-foreground">
                        {property?.title || 'عقار بدون عنوان'}
                      </h4>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Icon name="MapPin" size={14} />
                        <span>{property?.address || 'العنوان غير محدد'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Building" size={14} />
                        <span>{getPropertyTypeLabel(property?.propertyType)}</span>
                      </div>
                      {property?.price && (
                        <div className="flex items-center gap-2">
                          <Icon name="DollarSign" size={14} />
                          <span className="font-semibold text-primary">
                            {property?.price?.toLocaleString('ar-SA')} ريال
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        getStatusColor(property?.status)
                      }`}
                    >
                      {getStatusLabel(property?.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            إجمالي العقارات: <span className="font-bold text-primary">{filteredProperties?.length}</span>
          </p>
          <Button variant="outline" onClick={onClose}>
            إغلاق
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AllPropertiesModal;